// @ts-check
import { useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import { GST_STATE_NAMES } from '../../utils/gstStates';
import {
  WHOLESALE_CAPSULE_TIER,
  WHOLESALE_MODEL,
  WHOLESALE_PARTNER_KIND,
  WHOLESALE_PRICE_TIER,
  WHOLESALE_PRIORITY,
} from '../../utils/wholesale';
import Modal from '../ui/Modal';
import { EnumSelect, Field, input, primaryBtn, secondaryBtn } from './shared';

const blank = () => ({
  name: '',
  kind: 'boutique',
  city: '',
  state: '',
  website: '',
  instagram_handle: '',
  price_tier: 'unknown',
  capsule_tier: '',
  lead_model: 'sample_rail',
  lead_note: '',
  priority: 'now',
  next_action: '',
  next_action_on: '',
  source: '',
  notes: '',
  custom_paragraph: '',
  first_mail_subject: '',
  event_starts_on: '',
  apply_by: '',
  owner_group: '',
  application_first: false,
  application_url: '',
  application_checklist: '',
  legal_name: '',
  gstin: '',
  udyam_number: '',
  address: '',
});

/**
 * Add a partner, or edit one. Fields that are blank are sent as null so a cleared field clears.
 * @param {{partner?: any, onClose: () => void, onSaved: (partner: any) => void}} props
 */
const PartnerFormModal = ({ partner, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState(() => {
    if (!partner) return blank();
    const next = blank();
    Object.keys(next).forEach((key) => {
      next[key] = partner[key] ?? (key === 'application_first' ? false : '');
    });
    return next;
  });
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const set = (patch) => {
    setIsDirty(true);
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Enter the partner name');
    if (form.application_first && !form.application_url.trim()) {
      return toast.error('Add the application link before choosing to apply through a web form');
    }
    const body = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === 'string' && !v.trim() ? null : v]),
    );
    body.name = form.name.trim();
    setSaving(true);
    try {
      const saved = partner
        ? await adminWholesaleService.updatePartner(partner.id, body)
        : await adminWholesaleService.createPartner(body);
      toast.success(partner ? 'Partner updated' : 'Partner added');
      setIsDirty(false);
      onSaved(saved);
    } catch (err) {
      toast.error(err.message || 'Failed to save the partner');
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (isDirty && !window.confirm('Discard your changes?')) return;
    onClose();
  };

  return (
    <Modal maxWidth="2xl" onClose={close} hasUnsavedChanges={isDirty}>
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">{partner ? `Edit ${partner.name}` : 'Add partner'}</h2>
      </div>
      <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name *" hint="The trading name as the store uses it">
            <input className={input} value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Kind">
            <EnumSelect
              value={form.kind}
              onChange={(kind) => set({ kind })}
              options={Object.values(WHOLESALE_PARTNER_KIND)}
            />
          </Field>
          <Field label="City">
            <input
              className={input}
              placeholder="e.g. Mumbai, Khar"
              value={form.city}
              onChange={(e) => set({ city: e.target.value })}
            />
          </Field>
          <Field label="State" hint="Drives the place of supply on the invoice">
            <select className={input} value={form.state} onChange={(e) => set({ state: e.target.value })}>
              <option value="">Choose the state...</option>
              {GST_STATE_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Website">
            <input className={input} value={form.website} onChange={(e) => set({ website: e.target.value })} />
          </Field>
          <Field label="Instagram">
            <input
              className={input}
              placeholder="@handle"
              value={form.instagram_handle}
              onChange={(e) => set({ instagram_handle: e.target.value })}
            />
          </Field>
          <Field label="Store price tier" hint="From research; sets the capsule">
            <EnumSelect
              value={form.price_tier}
              onChange={(price_tier) => set({ price_tier })}
              options={Object.values(WHOLESALE_PRICE_TIER)}
            />
          </Field>
          <Field label="Capsule">
            <EnumSelect
              value={form.capsule_tier}
              onChange={(capsule_tier) => set({ capsule_tier })}
              options={Object.values(WHOLESALE_CAPSULE_TIER)}
              allowEmpty
              emptyLabel="Not set"
            />
          </Field>
          <Field label="Lead with">
            <EnumSelect
              value={form.lead_model}
              onChange={(lead_model) => set({ lead_model })}
              options={Object.values(WHOLESALE_MODEL)}
            />
          </Field>
          <Field label="Priority">
            <EnumSelect
              value={form.priority}
              onChange={(priority) => set({ priority })}
              options={Object.values(WHOLESALE_PRIORITY)}
            />
          </Field>
        </div>
        <Field label="How we lead" hint="e.g. A as a residency in JLF week">
          <input className={input} value={form.lead_note} onChange={(e) => set({ lead_note: e.target.value })} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Next action">
            <input className={input} value={form.next_action} onChange={(e) => set({ next_action: e.target.value })} />
          </Field>
          <Field label="Next action on">
            <input
              type="date"
              className={input}
              value={form.next_action_on}
              onChange={(e) => set({ next_action_on: e.target.value })}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Event starts" hint="Real date of the next event; drives the Outreach order">
            <input
              type="date"
              className={input}
              value={form.event_starts_on}
              onChange={(e) => set({ event_starts_on: e.target.value })}
            />
          </Field>
          <Field label="Apply by" hint="Application deadline, if there is one">
            <input
              type="date"
              className={input}
              value={form.apply_by}
              onChange={(e) => set({ apply_by: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Same owner group (optional)" hint="Partners with the same key are run by the same person">
          <input
            className={input}
            placeholder="e.g. nandini-rishi"
            value={form.owner_group}
            onChange={(e) => set({ owner_group: e.target.value })}
          />
        </Field>
        <div className="border border-border rounded-lg p-3 space-y-3">
          <label className="flex items-start gap-2 text-sm font-medium">
            <input
              type="checkbox"
              className="mt-1"
              aria-label="Apply via a web form or application (instead of email)"
              checked={form.application_first}
              onChange={(e) => set({ application_first: e.target.checked })}
            />
            <span>Apply via a web form or application (instead of email)</span>
          </label>
          <Field label="Application link" hint="A web address starting with http:// or https://">
            <input
              className={input}
              placeholder="https://"
              value={form.application_url}
              onChange={(e) => set({ application_url: e.target.value })}
            />
          </Field>
          <Field label="What they ask for (one item per line)">
            <textarea
              className={input}
              rows={4}
              value={form.application_checklist}
              onChange={(e) => set({ application_checklist: e.target.value })}
            />
          </Field>
        </div>
        <Field label="First-mail subject">
          <input
            className={input}
            value={form.first_mail_subject}
            onChange={(e) => set({ first_mail_subject: e.target.value })}
          />
        </Field>
        <Field label="Custom paragraph" hint="Merged into the base email and DM for this door">
          <textarea
            className={input}
            rows={3}
            value={form.custom_paragraph}
            onChange={(e) => set({ custom_paragraph: e.target.value })}
          />
        </Field>
        <Field label="Source" hint="Which research or referral brought the door">
          <input className={input} value={form.source} onChange={(e) => set({ source: e.target.value })} />
        </Field>
        <Field label="Notes">
          <textarea className={input} rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        </Field>
        <details className="border border-border rounded-lg px-3 py-2">
          <summary className="text-sm font-medium cursor-pointer">
            Legal details (filled from the first quote or signed sheet)
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <Field label="Legal name">
              <input className={input} value={form.legal_name} onChange={(e) => set({ legal_name: e.target.value })} />
            </Field>
            <Field label="GSTIN">
              <input
                className={input}
                value={form.gstin}
                onChange={(e) => set({ gstin: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Udyam number">
              <input
                className={input}
                value={form.udyam_number}
                onChange={(e) => set({ udyam_number: e.target.value })}
              />
            </Field>
            <Field label="Address">
              <input className={input} value={form.address} onChange={(e) => set({ address: e.target.value })} />
            </Field>
          </div>
        </details>
      </div>
      <div className="flex gap-2 px-6 py-4 border-t">
        <button onClick={save} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={close} disabled={saving} className={secondaryBtn}>
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default PartnerFormModal;
